import { ChatSessionStreamStateKind } from "@qw/graphql";

/**
 * Payload version this client is built for. Matches the server-side
 * `CHAT_SESSION_STREAM_STATE_FORMAT_VERSION` constant. Events with any
 * other version are ignored so the server can ship incompatible shape
 * changes (`v2`+) later without breaking older clients.
 */
export const EXPECTED_STREAM_STATE_FORMAT_VERSION = "v1";

/**
 * AI SDK `useChat` status union. Mirrored locally because `@ai-sdk/react`
 * doesn't export the type directly.
 */
export type AiSdkChatStatus = "ready" | "submitted" | "streaming" | "error";

/**
 * Discriminated decision for the `chatSessionStreamStateChanged`
 * subscription's `onData` handler.
 *
 * - `"resume"`          → call `resumeStream()` to attach the SSE.
 * - `"stop_and_record"` → call `stop()` to release `useChat.status`
 *                         from `submitted` / `streaming` AND surface
 *                         the failure error to the user.
 * - `"record_only"`     → surface the failure error WITHOUT calling
 *                         `stop()` (there's nothing to stop, but the
 *                         user should still see the message).
 * - `"none"`            → ignore the event.
 *
 * All actions share the format-version + session-match gates; the
 * differences are kind-specific:
 *
 * STREAMING → "resume"
 *   - Fires only on `kind === STREAMING`. The subscription delivers
 *     events for every transition (ENQUEUED, EXECUTING, COMPLETED,
 *     FAILED, etc.); only STREAMING means "go attach the SSE."
 *   - Suppresses when the AI SDK is already mid-POST or actively
 *     streaming (`submitted` / `streaming`). The server publishes
 *     STREAMING from `transitionToExecuting` — before the POST
 *     response has begun streaming back — so a normal user send
 *     observes `status === "submitted"` when the event arrives.
 *     Firing `resumeStream()` then would race a second GET against
 *     the POST that's already going to deliver the same chunks.
 *     Only re-fire from terminal hook states (`ready` after a
 *     finished/aborted turn, `error` for the stuck-error re-arm).
 *   - Suppresses when an elicit submit is in flight.
 *     `submitElicitationResponse` POSTs
 *     `/elicitations/:toolCallId/resolve` and then imperatively calls
 *     `resumeStream()`, but the AI SDK doesn't flip to `streaming`
 *     until the GET response starts delivering chunks. The server
 *     publishes STREAMING the moment `createStream` runs, which
 *     typically lands while the imperative GET is still in
 *     `waitForState`'s server-side poll loop and `hookStatus ===
 *     "ready"`. Without this gate, the subscription fires a SECOND
 *     `resumeStream()` that races the first, both consumers replay
 *     the stream's chunks, and the assistant message gets duplicate
 *     text parts (visible jank: the first word renders twice as
 *     separate blocks for ~seconds before the orphan disappears).
 *
 * FAILED → "stop_and_record" | "record_only"
 *   - Fires on `kind === FAILED` for any matching session+format,
 *     regardless of hook status. Brief 004: pre-admit failures and
 *     post-mint v2 hash transitions both publish FAILED; the user
 *     should always see the server's specific failure message in
 *     `useChat.error`, even when the AI SDK has no stream to stop.
 *   - "stop_and_record" when status is `submitted` / `streaming`:
 *     there's an active turn to abort, and the user should see the
 *     failure detail.
 *   - "record_only" when status is `ready` / `error`: nothing to
 *     stop (no active turn, or AI SDK's own onError already flipped
 *     us). Critical windows the record-only branch protects:
 *       * Elicit-submit: hook stays at `ready` between the
 *         imperative POST and the resumed GET's first chunk. A
 *         pre-admit failure during this window must still surface
 *         the error, not silently clear the elicit indicator.
 *       * SSE error → server FAILED: AI SDK flipped to `error` with
 *         a generic stream-ended message. Overriding via
 *         `streamFailureError` exposes the more-specific server
 *         message via `combinedError = streamFailureError ?? error`.
 *   - Does NOT suppress on elicit submit — if the server says the
 *     turn failed, that's authoritative regardless of which client
 *     flow was in flight.
 *
 * Every other kind (ENQUEUED, EXECUTING, COMPLETED, UNKNOWN, ...) →
 * "none". STREAMING is the entry signal; FAILED is the exit signal;
 * the rest are observable transitions the client doesn't need to act
 * on (the AI SDK's own stream-end handling covers COMPLETED).
 */
export type StreamStateEventAction = "resume" | "stop_and_record" | "record_only" | "none";

export const actionForStreamStateEvent = ({
  eventChatSessionId,
  eventKind,
  eventFormatVersion,
  sessionId,
  hookStatus,
  isElicitationSubmitting,
}: {
  eventChatSessionId: string | null | undefined;
  eventKind: ChatSessionStreamStateKind | null | undefined;
  eventFormatVersion: string | null | undefined;
  sessionId: string;
  hookStatus: AiSdkChatStatus;
  isElicitationSubmitting: boolean;
}): StreamStateEventAction => {
  if (eventFormatVersion !== EXPECTED_STREAM_STATE_FORMAT_VERSION) {
    return "none";
  }
  if (!eventChatSessionId || eventChatSessionId !== sessionId) {
    return "none";
  }

  if (eventKind === ChatSessionStreamStateKind.Streaming) {
    if (hookStatus === "submitted" || hookStatus === "streaming") {
      return "none";
    }
    if (isElicitationSubmitting) {
      return "none";
    }
    return "resume";
  }

  if (eventKind === ChatSessionStreamStateKind.Failed) {
    if (hookStatus === "submitted" || hookStatus === "streaming") {
      return "stop_and_record";
    }
    return "record_only";
  }

  return "none";
};
